import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, withQuery } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    ReviewableProjectsResponseSchema,
    ReviewRequestSchema,
    ExpertReviewSchema,
    ExpertReviewsStatsSchema,
    ReviewRequestCreationResponseSchema,
    ReviewResponseUpdateResponseSchema,
    SendReminderResponseSchema,
    UserSearchResponseSchema,
    ProjectValueRangesResponseSchema,
    type UserSearchQueryParams,
    type ExpertReviewRequestsQueryParams,
    type ExpertReviewsQueryParams,
    type CreateReviewRequestForExistingClientBody,
    type CreateReviewRequestForNewClientBody,
    type SendReminderBody,
    type RespondToReviewBody,
} from "../schemas/reviews-v2";


export function createExpertReviewsV2Api(http: HttpClient, baseUrl: string) {
    const expertBaseUrl = `${baseUrl}/expert/reviews-v2`;
    const reviewRequestsBaseUrl = `${baseUrl}/expert/review-requests-v2`;
    
    return {
        async getClientProjects(args: { params: { clientId: number }; query?: { page?: number } }) {
            const response = await http.get<unknown>(
                withQuery(`${expertBaseUrl}/clients/${args.params.clientId}/projects`, args.query),
            );
            return ReviewableProjectsResponseSchema.parse(response.data);
        },

        createReviewRequestForExistingClient: createEndpoint<
            z.infer<typeof ReviewRequestCreationResponseSchema>,
            CreateReviewRequestForExistingClientBody,
            QueryParams
        >(http, "POST", "/", {
            base: reviewRequestsBaseUrl,
            responseSchema: ReviewRequestCreationResponseSchema,
        }),

        createReviewRequestForNewClient: createEndpoint<
            z.infer<typeof ReviewRequestCreationResponseSchema>,
            CreateReviewRequestForNewClientBody,
            QueryParams
        >(http, "POST", "/", {
            base: reviewRequestsBaseUrl,
            responseSchema: ReviewRequestCreationResponseSchema,
        }),

        getReviewRequests: createEndpointWithPagination<
            z.infer<typeof ReviewRequestSchema>,
            unknown,
            unknown,
            ExpertReviewRequestsQueryParams
        >(http, "GET", "/", {
            base: reviewRequestsBaseUrl,
            itemSchema: ReviewRequestSchema,
        }),

        sendReminder: createEndpoint<
            z.infer<typeof SendReminderResponseSchema>,
            SendReminderBody,
            QueryParams,
            { reviewRequest: number }
        >(http, "POST", "/:reviewRequest/reminder", {
            base: reviewRequestsBaseUrl,
            responseSchema: SendReminderResponseSchema,
            params: ["reviewRequest"],
        }),

        getReviews: createEndpointWithPagination<
            z.infer<typeof ExpertReviewSchema>,
            z.infer<typeof ExpertReviewsStatsSchema>,
            unknown,
            ExpertReviewsQueryParams
        >(http, "GET", "/", {
            base: expertBaseUrl,
            itemSchema: ExpertReviewSchema,
            metaSchema: ExpertReviewsStatsSchema,
        }),

        respondToReview: createEndpoint<
            z.infer<typeof ReviewResponseUpdateResponseSchema>,
            RespondToReviewBody,
            QueryParams,
            { review: number }
        >(http, "POST", "/:review/response", {
            base: expertBaseUrl,
            responseSchema: ReviewResponseUpdateResponseSchema,
            params: ["review"],
        }),

        updateResponse: createEndpoint<
            z.infer<typeof ReviewResponseUpdateResponseSchema>,
            RespondToReviewBody,
            QueryParams,
            { review: number }
        >(http, "PUT", "/:review/response", {
            base: expertBaseUrl,
            responseSchema: ReviewResponseUpdateResponseSchema,
            params: ["review"],
        }),

        searchUsers: createEndpoint<
            z.infer<typeof UserSearchResponseSchema>,
            unknown,
            UserSearchQueryParams
        >(http, "GET", "/users/search", {
            base: expertBaseUrl,
            responseSchema: UserSearchResponseSchema,
        }),

        getProjectValueRanges: createEndpoint<
            z.infer<typeof ProjectValueRangesResponseSchema>,
            unknown,
            QueryParams
        >(http, "GET", "/project-value-ranges", {
            base: expertBaseUrl,
            responseSchema: ProjectValueRangesResponseSchema,
        }),
    };
}

